import { useState, useCallback, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { CalendarBlank, MapPin, Users, Fire, ArrowUpRight } from "@phosphor-icons/react";
import SEO from "@/components/SEO";
import AINavBar from "@/components/AINavBar";
import { useInfiniteFeed } from "@/hooks/useInfiniteFeed";
import { fetchAISection } from "@/lib/api";
import { stripHtml } from "@/lib/stripHtml";

const EVENT_CATEGORIES = ["All", "Conference", "Summit", "Workshop", "Webinar", "Hackathon", "Meetup", "Product Launch", "Festival"];
const EVENT_FORMATS = ["All", "In-person", "Virtual", "Hybrid"];

function SkeletonCard() {
  return (
    <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden animate-pulse">
      <div className="h-20 bg-slate-200" />
      <div className="p-5">
        <div className="flex gap-2 mb-3">
          <div className="h-5 bg-slate-200 rounded-full w-20" />
          <div className="h-5 bg-slate-200 rounded-full w-16" />
        </div>
        <div className="h-4 bg-slate-200 rounded w-3/4 mb-2" />
        <div className="h-3 bg-slate-200 rounded w-full mb-2" />
        <div className="h-3 bg-slate-200 rounded w-1/2" />
      </div>
    </div>
  );
}

function EventCard({ event }) {
  const slug = event.slug || event.id;
  const typeColors = {
    "Conference":    "bg-blue-50 text-blue-700 border-blue-200",
    "Product Launch":"bg-purple-50 text-purple-700 border-purple-200",
    "Summit":        "bg-amber-50 text-amber-700 border-amber-200",
    "Festival":      "bg-rose-50 text-rose-700 border-rose-200",
    "Workshop":      "bg-cyan-50 text-cyan-700 border-cyan-200",
    "Webinar":       "bg-indigo-50 text-indigo-700 border-indigo-200",
    "Hackathon":     "bg-emerald-50 text-emerald-700 border-emerald-200",
    "Meetup":        "bg-teal-50 text-teal-700 border-teal-200",
  };
  const formatColors = {
    "In-person":          "bg-slate-100 text-slate-600",
    "Virtual":            "bg-indigo-50 text-indigo-700",
    "Hybrid":             "bg-emerald-50 text-emerald-700",
    "In-person + Stream": "bg-teal-50 text-teal-700",
  };

  const startDate = new Date(event.date);
  const endDate = (event.endDate || event.end_date) ? new Date(event.endDate || event.end_date) : null;
  const isPast = startDate < new Date();
  const format = event.format || (event.location?.toLowerCase().includes("virtual") ? "Virtual" : "In-person");
  const isFeatured = event.featured || event.isFeatured || event.is_featured;
  const isHot = event.hot || event.is_hot;
  const website = event.website || event.url || "";

  return (
    <Link to={`/trending-ai/events/${slug}`} className={`block bg-white border rounded-2xl overflow-hidden hover:border-blue-300 hover:shadow-md transition-all group ${
      isFeatured ? "border-blue-300 ring-1 ring-blue-100" : "border-slate-200"
    } ${isPast ? "opacity-60" : ""}`}>
      <div className="bg-gradient-to-r from-slate-900 to-blue-900 text-white px-5 py-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-xs font-medium text-slate-400 uppercase tracking-wider">
              {startDate.toLocaleString("default", { month: "long" })} {startDate.getFullYear()}
            </div>
            <div className="text-2xl font-black">
              {startDate.getDate()}
              {endDate && ` – ${endDate.getDate()}`}
            </div>
          </div>
          <div className="flex flex-col items-end gap-1">
            {isHot && (
              <span className="text-[10px] font-bold bg-red-500 text-white px-1.5 py-0.5 rounded flex items-center gap-0.5">
                <Fire size={7} weight="fill" /> HOT
              </span>
            )}
            {isFeatured && (
              <span className="text-[10px] font-bold bg-amber-400 text-slate-900 px-1.5 py-0.5 rounded">⭐ FEATURED</span>
            )}
            {isPast && <span className="text-[10px] font-bold bg-slate-600 text-white px-1.5 py-0.5 rounded">PAST</span>}
          </div>
        </div>
      </div>

      <div className="p-5">
        <div className="flex flex-wrap gap-2 mb-3">
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${typeColors[event.type] || "bg-slate-100 text-slate-600 border-slate-200"}`}>
            {event.type || "Event"}
          </span>
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${formatColors[format] || "bg-slate-100 text-slate-600"}`}>
            {format}
          </span>
          {(event.isFree || event.is_free) && (
            <span className="text-[10px] font-semibold bg-emerald-50 text-emerald-700 px-2 py-0.5 rounded-full">Free</span>
          )}
        </div>

        <h3 className="text-base font-bold text-slate-900 group-hover:text-blue-600 transition-colors mb-2">
          {stripHtml(event.name)}
        </h3>

        <p className="text-xs text-slate-600 leading-relaxed mb-4">{stripHtml(event.description)}</p>

        <div className="flex items-center gap-3 text-xs text-slate-500 mb-4">
          <span className="flex items-center gap-1"><MapPin size={11} /> {event.location}</span>
          {event.attendees && <span className="flex items-center gap-1"><Users size={11} /> {event.attendees}</span>}
        </div>

        {event.speakers && event.speakers.length > 0 && (
          <div className="mb-4">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Featured Speakers</p>
            <div className="flex flex-wrap gap-1">
              {event.speakers.map((s) => (
                <span key={s} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{s}</span>
              ))}
            </div>
          </div>
        )}

        {event.tags && event.tags.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-4">
            {event.tags.map((t) => (
              <span key={t} className="text-[10px] bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full">{t}</span>
            ))}
          </div>
        )}

        <div className="flex items-center justify-between border-t border-slate-100 pt-3">
          <span className="text-xs font-medium text-slate-500">{event.category || event.type}</span>
          {website && (
            <span className="flex items-center gap-1 text-xs font-medium text-blue-600">
              Learn more <ArrowUpRight size={11} />
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}

export default function AIEventsPage() {
  const [cat, setCat] = useState("All");
  const [format, setFormat] = useState("All");
  const [showPast, setShowPast] = useState(false);

  const fetchFn = useCallback(
    (page) => fetchAISection("events", { page, page_size: 12 }),
    []
  );
  const { items: apiItems, loading, loadingMore, hasMore, error, loadMore } = useInfiniteFeed({
    fetchFn,
    feedKey: "ai-events",
    getCacheKey: (page) => `ai-events-p${page}`,
    ttlMs: 10 * 60 * 1000,
  });

  const sentinelRef = useRef(null);
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) loadMore(); }, { rootMargin: "300px" });
    obs.observe(el);
    return () => obs.disconnect();
  }, [loadMore]);

  const allItems = apiItems;
  const now = new Date();

  const filtered = allItems
    .filter((e) => {
      const typeMatch = cat === "All" || e.type === cat;
      const fmtVal = e.format || (e.location?.toLowerCase().includes("virtual") ? "Virtual" : "In-person");
      const formatMatch = format === "All" || fmtVal.includes(format);
      const pastMatch = showPast ? true : new Date(e.date) >= now;
      return typeMatch && formatMatch && pastMatch;
    })
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const upcoming = allItems.filter((e) => new Date(e.date) >= now).length;

  return (
    <>
      <SEO
        title="AI Events — Conferences, Summits & Product Launches · TrendMing"
        description="AI conference calendar: NeurIPS, ICLR, OpenAI DevDay, Google I/O, AI Summit and more. Find upcoming AI events, workshops, and product launches."
        canonical="https://trendming.com/trending-ai/events"
      />
      <AINavBar />
      <div className="max-w-[1400px] mx-auto px-4 md:px-6 py-6">
        <div className="mb-6">
          <span className="text-xs font-semibold uppercase tracking-wider text-blue-600">AI Intelligence · Events</span>
          <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mt-1">AI Event Calendar</h1>
          <p className="text-slate-500 text-sm mt-1">Upcoming AI conferences, product launches, summits, and community events.</p>
        </div>

        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: "Upcoming Events", value: upcoming },
            { label: "Total Events", value: allItems.length },
            { label: "Featured", value: allItems.filter((e) => e.featured || e.isFeatured).length },
          ].map((s) => (
            <div key={s.label} className="bg-white border border-slate-200 rounded-xl p-3 text-center">
              <div className="text-xl font-bold text-slate-900">{s.value}</div>
              <div className="text-xs text-slate-400">{s.label}</div>
            </div>
          ))}
        </div>

        <div className="flex flex-col sm:flex-row gap-3 mb-6 flex-wrap">
          <select value={cat} onChange={(e) => setCat(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
            {EVENT_CATEGORIES.map((c) => <option key={c}>{c}</option>)}
          </select>
          <select value={format} onChange={(e) => setFormat(e.target.value)} className="px-3 py-2 text-sm border border-slate-200 rounded-lg bg-white focus:outline-none focus:ring-2 focus:ring-blue-500/30">
            {EVENT_FORMATS.map((f) => <option key={f}>{f}</option>)}
          </select>
          <label className="flex items-center gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={showPast}
              onChange={(e) => setShowPast(e.target.checked)}
              className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
            />
            <span className="text-sm text-slate-600">Show past events</span>
          </label>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
          {loading && Array.from({ length: 6 }).map((_, i) => <SkeletonCard key={i} />)}
          {!loading && filtered.map((event) => <EventCard key={event.id} event={event} />)}
          {!loading && filtered.length === 0 && (
            <div className="col-span-full text-center py-16 text-slate-400">
              <p className="text-lg font-medium">No events found</p>
            </div>
          )}
        </div>

        {hasMore && !loading && <div ref={sentinelRef} className="h-4" />}
        {loadingMore && <div className="text-center py-4 text-xs text-slate-400">Loading more…</div>}
        {error && !loading && (
          <div className="text-center py-8 text-slate-500">
            <p className="text-red-500 font-medium">Could not load events</p>
            <p className="text-xs mt-1">Content is being refreshed — check back shortly.</p>
          </div>
        )}
      </div>
    </>
  );
}
